import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Popover,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Iconify from "~src/components/Iconify";
import { engagementRangeByKey, findLabelByValues, handleDelete } from "./utils";
import { BrandMappingFilters } from "~src/hooks/useBrandMappingV3";
import MultiCountrySelect from "~src/components/MultiCountrySelect";
import NumberRangeSelector from "~src/components/NumberRangeSelector";
import { CountryType } from "~src/data/countries";

export default function FilterChips({
  filters,
  onFilterUpdate,
  maxFollowers,
  brandCountries,
}: {
  filters?: BrandMappingFilters | undefined;
  onFilterUpdate?: (filters: BrandMappingFilters | undefined) => void;
  maxFollowers: number | undefined;
  brandCountries: CountryType[] | undefined;
}) {
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentSelector, setCurrentSelector] = useState<string>();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleApply = () => {
    if (localFilters) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onFilterUpdate && onFilterUpdate(localFilters);
      handleClose();
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalFilters((prevFilters) => ({
      ...prevFilters,
      has_running_ads: event.target.checked,
    }));
  };

  function renderSelector(value: string | undefined) {
    switch (value) {
      case "brand_country":
        return (
          <MultiCountrySelect
            title="Brand Country"
            value={filters?.brand_country}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                brand_country: value,
              }));
            }}
            countries={brandCountries}
          />
        );

      case "has_running_ads":
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={localFilters?.has_running_ads}
                onChange={handleCheckboxChange}
              />
            }
            label="Has Matching Ads"
          />
        );

      case "ad_country":
        return (
          <MultiCountrySelect
            title="Ad Country"
            value={filters?.ad_country}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                ad_country: value,
              }));
            }}
          />
        );
      case "product_price_range":
        return (
          <NumberRangeSelector
            title="Product Price"
            value={localFilters?.product_price_range}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                product_price_range: value,
              }));
            }}
          />
        );
      case "estimated_monthly_revenue":
        return (
          <NumberRangeSelector
            title="Estimated Monthly Revenue"
            value={localFilters?.estimated_monthly_revenue}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                estimated_monthly_revenue: value,
              }));
            }}
          />
        );
      case "estimated_monthly_traffic":
        return (
          <NumberRangeSelector
            title="Estimated Site Traffic"
            value={localFilters?.estimated_monthly_traffic}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                estimated_monthly_traffic: value,
              }));
            }}
          />
        );

      case "social_followers":
        return (
          <NumberRangeSelector
            title="Social Followers"
            value={localFilters?.social_followers}
            onSelect={(value) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                social_followers: value,
              }));
            }}
            rangeOptions={[
              { label: "Low Followers", min: 0, max: 10000 },
              { label: "Medium Followers", min: 10001, max: 50000 },
              {
                label: "High Followers",
                min: 50001,
                max: maxFollowers || 100000,
              },
            ]}
          />
        );
      default:
        return null;
    }
  }

  return (
    <Box>
      {filters?.brand_country && (
        <Button
          aria-describedby={id}
          onClick={(e) => {
            setCurrentSelector("brand_country");
            handleClick(e);
          }}
          sx={{
            "&:hover": {
              backgroundColor: "inherit",
            },
          }}
        >
          <Chip
            sx={{ px: 1, py: 2 }}
            label={`Brand Countries: ${findLabelByValues(filters.brand_country, true)}`}
            variant="outlined"
            onDelete={() =>
              handleDelete("brand_country", filters, onFilterUpdate)
            }
            avatar={<Iconify icon={"gis:search-country"} />}
          />
        </Button>
      )}

      {filters?.has_running_ads && (
        <Button
          aria-describedby={id}
          onClick={(e) => {
            setCurrentSelector("has_running_ads");
            handleClick(e);
          }}
          sx={{
            "&:hover": {
              backgroundColor: "inherit",
            },
          }}
        >
          <Chip
            sx={{ px: 1, py: 2 }}
            label={"Running Ads"}
            variant="outlined"
            onDelete={() =>
              handleDelete("has_running_ads", filters, onFilterUpdate)
            }
            avatar={<Iconify icon={"streamline:target"} />}
          />
        </Button>
      )}

      {filters?.ad_country && (
        <Button
          aria-describedby={id}
          onClick={(e) => {
            setCurrentSelector("ad_country");
            handleClick(e);
          }}
          sx={{
            "&:hover": {
              backgroundColor: "inherit",
            },
          }}
        >
          <Chip
            sx={{ px: 1, py: 2 }}
            label={`Ad countries: ${findLabelByValues(filters.ad_country)}`}
            variant="outlined"
            onDelete={() => handleDelete("ad_country", filters, onFilterUpdate)}
            avatar={<Iconify icon={"gis:search-country"} />}
          />
        </Button>
      )}

      {filters?.product_price_range && (
        <Button
          aria-describedby={id}
          onClick={(e) => {
            setCurrentSelector("product_price_range");
            handleClick(e);
          }}
          sx={{
            "&:hover": {
              backgroundColor: "inherit",
            },
          }}
        >
          <Chip
            sx={{ px: 1, py: 2 }}
            label={engagementRangeByKey(filters.product_price_range)}
            variant="outlined"
            onDelete={() =>
              handleDelete("product_price_range", filters, onFilterUpdate)
            }
            avatar={<Iconify icon={"ion:pricetags-outline"} />}
          />
        </Button>
      )}

      {filters?.estimated_monthly_revenue && (
        <Button
          aria-describedby={id}
          onClick={(e) => {
            setCurrentSelector("estimated_monthly_revenue");
            handleClick(e);
          }}
          sx={{
            "&:hover": {
              backgroundColor: "inherit",
            },
          }}
        >
          <Chip
            sx={{ px: 1, py: 2 }}
            label={engagementRangeByKey(filters.estimated_monthly_revenue)}
            variant="outlined"
            onDelete={() =>
              handleDelete("estimated_monthly_revenue", filters, onFilterUpdate)
            }
            avatar={<Iconify icon={"game-icons:cash"} />}
          />
        </Button>
      )}

      {filters?.estimated_monthly_traffic && (
        <Button
          aria-describedby={id}
          onClick={(e) => {
            setCurrentSelector("estimated_monthly_traffic");
            handleClick(e);
          }}
          sx={{
            "&:hover": {
              backgroundColor: "inherit",
            },
          }}
        >
          <Chip
            sx={{ px: 1, py: 2 }}
            label={engagementRangeByKey(filters.estimated_monthly_traffic)}
            variant="outlined"
            onDelete={() =>
              handleDelete("estimated_monthly_traffic", filters, onFilterUpdate)
            }
            avatar={<Iconify icon={"material-symbols:web-traffic"} />}
          />
        </Button>
      )}

      {filters?.social_followers && (
        <Button
          aria-describedby={id}
          onClick={(e) => {
            setCurrentSelector("social_followers");
            handleClick(e);
          }}
          sx={{
            "&:hover": {
              backgroundColor: "inherit",
            },
          }}
        >
          <Chip
            sx={{ px: 1, py: 2 }}
            label={engagementRangeByKey(filters.social_followers)}
            variant="outlined"
            onDelete={() =>
              handleDelete("social_followers", filters, onFilterUpdate)
            }
            avatar={<Iconify icon={"clarity:thumbs-up-line"} />}
          />
        </Button>
      )}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2 }}>
          {renderSelector(currentSelector)}
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", marginTop: 1 }}
          >
            <Button variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
